import React, {useState} from 'react';
import dayjs from 'dayjs';
import {deepPurple, purple} from '@mui/material/colors';
import {Box, Button, Dialog, Link, Paper, Stack, Typography} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {StyleSx, Sx} from 'app/types/common';
import {packSx} from 'app/util/packSx/packSx';
import {DialogClose} from 'app/components/sharedReactComponents/DialogClose/DialogClose';

const rootSx: StyleSx = {
  p: 1,
  color: 'white',
  height: 50,
  background:
    'linear-gradient(310.64deg, #283E6D 8.53%, rgba(93, 68, 193, 0.92) 74.31%, rgba(154, 58, 187, 0.7) 106.98%)',
};

const paperSx: StyleSx = {mb: 2};
const headerSx: StyleSx = {
  height: 120,
  p: 3,
  background: `linear-gradient(350deg, ${purple[500]} 0%, ${deepPurple[700]} 50%)`,
};
const iconSx: StyleSx = {color: deepPurple[600]};

interface Props extends Sx {
  date: string;
}

export default function BillingChangeNotice({sx, date}: Props) {
  const [open, setOpen] = useState(false);

  const formatted = dayjs(date).format('ll');

  return (
    <>
      <Stack
        sx={packSx(rootSx, sx)}
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap={1}
      >
        <AccessTimeIcon />

        <Typography>
          Heads up, your <b>Epiphan Edge</b> billing is changing {formatted}
        </Typography>

        <Button variant="outlined" color="inherit" onClick={() => setOpen(true)}>
          Learn more
        </Button>
      </Stack>

      <Dialog open={open} maxWidth="sm" fullWidth={true} onClose={() => setOpen(false)}>
        <DialogClose onClose={() => setOpen(false)} />

        <Box sx={headerSx}>
          <Box
            component="img"
            src="/assets/img/logo/logo.png"
            srcSet="/assets/img/logo/logo@x2.png 2x"
            alt="Epiphan Cloud logo"
          />
        </Box>

        <Box px={3} py={4}>
          <Typography variant="h5" fontWeight={700} mb={1.5}>
            Upcoming changes to Epiphan Edge billing
          </Typography>

          <Typography mb={3}>
            Starting <b>{formatted}</b> we will update our billing process for more flexibility.
          </Typography>

          <Message sx={paperSx} icon={<EventIcon sx={iconSx} />} title="Billing process update">
            <Typography>
              You will now be billed at the beginning of each billing cycle based on the number of
              devices currently paired with your team.
            </Typography>
          </Message>

          <Message
            sx={paperSx}
            icon={<PlaylistAddIcon sx={iconSx} />}
            title="Device change adjustments"
          >
            <Typography>
              If the number of paired devices decreases during the billing cycle, charges will be
              prorated, and a revised invoice will reflect any applicable credit for the difference.
            </Typography>

            <Typography>
              If the number of paired devices increases during the billing cycle, charges will be
              prorated, and a new invoice will reflect the adjusted amount, accounting for payments
              already made.
            </Typography>
          </Message>

          <Typography mt={3}>
            For any questions, please reach out to our support team at{' '}
            <Link href="mailto:info@epiphan.com">info@epiphan.com</Link>
          </Typography>
        </Box>
      </Dialog>
    </>
  );
}

const messageSx: StyleSx = {p: 1};

interface MessageProps extends Sx {
  icon: React.ReactNode;
  title: string;
}

function Message({sx, icon, title, children}: React.PropsWithChildren<MessageProps>) {
  return (
    <Paper sx={packSx(messageSx, sx)} variant="outlined">
      <Stack direction="row" alignItems="center" gap={1}>
        {icon}

        <Typography fontWeight={700} fontSize={16}>
          {title}
        </Typography>
      </Stack>

      <Box ml={3.75}>{children}</Box>
    </Paper>
  );
}
